import type {
    CampaignSubmission,
    CampaignSubmissionStatus,
    MetaAdMetaKey,
} from '@/api/campaigns/types';

/**
 * Whether a submission can be reprocessed from its current status
 * (quantix-api/docs/campaigns/TASKS.md, W4: "POST .../reprocess ... failed/pending only";
 * same rule applies to the bulk "reprocess failed" action's per-row button).
 */
export function canReprocessSubmission(status: CampaignSubmissionStatus): boolean {
    return status === 'failed' || status === 'pending';
}

/** Combined count of warnings + errors recorded on a submission, for the table's issue column. */
export function getSubmissionIssueCount(
    submission: Pick<CampaignSubmission, 'warnings' | 'errors'>
): number {
    return (submission.warnings?.length ?? 0) + (submission.errors?.length ?? 0);
}

/** CRM route for the record a submission points at: customers have their own page, leads use /leads. */
export function getSubmissionLeadPath(
    submission: Pick<CampaignSubmission, 'lead_id' | 'lead'>
): string | null {
    if (!submission.lead_id) return null;
    return submission.lead?.type === 'CUSTOMER'
        ? `/customers/${submission.lead_id}`
        : `/leads/${submission.lead_id}`;
}

/** A Meta ad attribution value (ad name, ad set id, ...) from a submission's meta, or null when absent. */
export function getMetaAdValue(
    submission: Pick<CampaignSubmission, 'meta'>,
    key: MetaAdMetaKey
): string | null {
    const value = submission.meta?.[key];
    return typeof value === 'string' && value !== '' ? value : null;
}
